const { NavLink: Link } = require('react-router-dom');
const T = require('prop-types');
const { default: Styled, createGlobalStyle } = require('styled-components');

const { default: Tab } = require('@mui/material/Tab');
const { default: Tabs } = require('@mui/material/Tabs');
const Avatar = require('components/Gamification/WeeklyAvatar');
const Gamification = require('components/Gamification');
const { default: Paper } = require('@mui/material/Paper');
const { default: Tooltip } = require('@mui/material/Tooltip');

const { default: DiscoverIcon } = require('@mui/icons-material/PersonSearch');
const { default: ConnectionsIcon } = require('@mui/icons-material/Forum');
const { default: GroupsIcon } = require('@mui/icons-material/Groups');
const { default: LocalActivityIcon } = require('@mui/icons-material/LocalActivity');
const { default: MenuBookIcon } = require('@mui/icons-material/MenuBook');

const { darken, useTheme } = require('@mui/material/styles');

const { getSizedImageUrl } = require('utils/image');

const {
    ELEMENT_IDS,
    MAIN_FOOTER_HEIGHT,
    FOOTER_TAB_HEIGHT,
    USER_ROLE_IDS
} = require('utils/constants');

const UnreadDMsBadge = require('../../routes/app/containers/UnreadDMsBadge');
const UnreadClassMessagesBadge = require('../../routes/app/containers/UnreadClassMessagesBadge');
const UnreadMessagesBadge = require('../../routes/app/components/UnreadMessagesBadge');

const internals = {};

const isInternalNearpeerEmail = (email) => {

    const parts = String(email || '').trim().toLowerCase().split('@');
    return parts.length === 2 && (parts[1] === 'nearpeer.net' || parts[1] === 'nearpeer.com');
};

module.exports = function AppFooterTabs(props) {

    const {
        location: { pathname: path },
        currentUser,
        currentSchool,
        unreadOffersCount = 0,
        hasOffers = false,
        unreadResourcesCount = 0,
        debug = {}
    } = props;

    const showOffers = !!currentSchool?.offersEnabled && hasOffers && (
        USER_ROLE_IDS.STUDENT.includes(currentUser?.roleId) ||
        (USER_ROLE_IDS.SUPERUSER.includes(currentUser?.roleId) && isInternalNearpeerEmail(currentUser?.email))
    );
    const showResources = USER_ROLE_IDS.PROSPECT.includes(currentUser?.roleId);

    const {
        Wrapper,
        ProfileLinks,
        AppChromeBorderStyles,
        StyledPaper,
        TabsInnerWrapper,
        IconWrapper,
        StyledUnreadDMsBadge,
        StyledUnreadClassMessagesBadge,
        StyledUnreadOffersBadge,
        StyledUnreadResourcesBadge
    } = internals;

    const profilePath = `/app/profile/${currentUser.id}`;
    const showFooterPoints = currentSchool?.id === 240394 && currentUser?.roleId === 2;
    const ProfilePhotoLink = showFooterPoints ? Link : 'span';

    const isDiscover = path.startsWith('/app/discover');
    const isDMs = path.startsWith('/app/messaging');
    const isGroups = path.startsWith('/app/classes');
    const isOffers = path.startsWith('/app/offers');
    const isResources = path.startsWith('/app/resources');
    const isProfile = path === profilePath;

    const theme = useTheme();

    // If we're not on any of these paths, deselect all tabs
    let tab = false;

    if (isDiscover) {
        tab = 'discover';
    }
    else if (isDMs) {
        tab = 'dms';
    }
    else if (isGroups) {
        tab = 'groups';
    }
    else if (isOffers) {
        tab = 'offers';
    }
    else if (isResources) {
        tab = 'resources';
    }
    else if (isProfile) {
        tab = 'profile';
    }

    return (
        <Wrapper>
            <AppChromeBorderStyles />
            {/*
                elevation is 0 to remove the shadow, since we need to set
                bottom padding to prevent overlap of iOS/Android phone UI overlapping our UI.
                Details are in PhoneSafeAreaInsetWrapper in src/containers/App
            */}
            <StyledPaper elevation={0} id={ELEMENT_IDS.appBottomTabs}>
                <TabsInnerWrapper
                    className='contentWrapper'
                    role='navigation'
                    aria-label='main'
                >
                    <Tabs
                        value={tab}
                        variant='fullWidth'
                    >
                        <Tab
                            LinkComponent={Link}
                            component={showFooterPoints ? 'div' : undefined}
                            to={showFooterPoints ? undefined : profilePath}
                            role={showFooterPoints ? 'presentation' : 'tab'}
                            disableRipple={showFooterPoints}
                            value='profile'
                            tabIndex={showFooterPoints ? -1 : 0}
                            aria-label='My Profile'
                            icon={
                                <ProfileLinks $compact={showFooterPoints}>
                                <Tooltip title='Profile' placement='top' arrow>
                                    <ProfilePhotoLink to={showFooterPoints ? profilePath : undefined} aria-label={showFooterPoints ? 'View my profile' : undefined}><IconWrapper>
                                        <Avatar userId={currentUser?.id}
                                            src={getSizedImageUrl(currentUser.croppedPicture, 100)}
                                            alt='Your profile'
                                            style={{
                                                width: showFooterPoints ? 28 : theme.constants.iconSize,
                                                height: showFooterPoints ? 28 : theme.constants.iconSize,
                                                border: '2px solid rgba(255, 255, 255, 0.8)',
                                                boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)',
                                                backgroundColor: theme.palette.primary.main,
                                                color: '#ffffff',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </IconWrapper></ProfilePhotoLink>
                                </Tooltip>
                                {showFooterPoints && <Gamification mode='footer' isMe />}
                                </ProfileLinks>
                            }
                            // label={<TabLabel>You</TabLabel>}
                            sx={{ minHeight: 'unset', padding: '8px 0' }}
                            data-focus-outline='inside,radius:5'
                        />
                        <Tab
                            LinkComponent={Link}
                            to='/app/discover'
                            value='discover'
                            tabIndex={0}
                            aria-label='Discover'
                            icon={
                                <Tooltip title='Discover' placement='top' arrow>
                                    <IconWrapper>
                                        <DiscoverIcon sx={{ fontSize: 34 }} />
                                    </IconWrapper>
                                </Tooltip>
                            }
                            // label={<TabLabel>Discover</TabLabel>}
                            sx={{ minHeight: 'unset', padding: '8px 0' }}
                            data-focus-outline='inside,radius:5'
                        />
                        <Tab
                            LinkComponent={Link}
                            to='/app/messaging'
                            value='dms'
                            tabIndex={0}
                            aria-label='Messages'
                            icon={
                                <Tooltip title='Messages' placement='top' arrow>
                                    <IconWrapper>
                                        <StyledUnreadDMsBadge>
                                            <ConnectionsIcon sx={{ fontSize: 29, marginTop: '2px' }} />
                                        </StyledUnreadDMsBadge>
                                    </IconWrapper>
                                </Tooltip>
                            }
                            sx={{ minHeight: 'unset', padding: '8px 0' }}
                            data-focus-outline='inside,radius:5'
                        />
                        <Tab
                            LinkComponent={Link}
                            to='/app/classes'
                            value='groups'
                            tabIndex={0}
                            aria-label='Groups'
                            icon={
                                <Tooltip title='Groups' placement='top' arrow>
                                    <IconWrapper>
                                        <StyledUnreadClassMessagesBadge>
                                            <GroupsIcon sx={{ fontSize: 40 }} />
                                        </StyledUnreadClassMessagesBadge>
                                    </IconWrapper>
                                </Tooltip>
                            }
                            sx={{ minHeight: 'unset', padding: '8px 0' }}
                            data-focus-outline='inside,radius:5'
                        />
                        {showOffers && (
                            <Tab
                                LinkComponent={Link}
                                to='/app/offers'
                                value='offers'
                                tabIndex={0}
                                aria-label={unreadOffersCount > 0 ? 'My Offers, new offers available' : 'My Offers'}
                                icon={
                                    <Tooltip title='My Offers' placement='top' arrow>
                                        <IconWrapper>
                                            <StyledUnreadOffersBadge numUnreads={unreadOffersCount > 0 ? 'NEW' : 0}>
                                                <LocalActivityIcon sx={{ fontSize: 32 }} />
                                            </StyledUnreadOffersBadge>
                                        </IconWrapper>
                                    </Tooltip>
                                }
                                sx={{ minHeight: 'unset', padding: '8px 0' }}
                                data-focus-outline='inside,radius:5'
                            />
                        )}
                        {showResources && (
                            <Tab
                                LinkComponent={Link}
                                to='/app/resources'
                                value='resources'
                                tabIndex={0}
                                aria-label={unreadResourcesCount > 0 ? 'Resources, new resources available' : 'Resources'}
                                icon={
                                    <Tooltip title='Resources' placement='top' arrow>
                                        <IconWrapper>
                                            <StyledUnreadResourcesBadge numUnreads={unreadResourcesCount > 0 ? 'NEW' : 0}>
                                                <MenuBookIcon sx={{ fontSize: 32 }} />
                                            </StyledUnreadResourcesBadge>
                                        </IconWrapper>
                                    </Tooltip>
                                }
                                sx={{ minHeight: 'unset', padding: '8px 0' }}
                                data-focus-outline='inside,radius:5'
                            />
                        )}
                    </Tabs>
                </TabsInnerWrapper>
            </StyledPaper>
        </Wrapper>
    );
};

module.exports.propTypes = {
    location: T.shape({
        pathname: T.string.isRequired
    }),
    currentUser: T.object,
    currentSchool: T.object,
    unreadOffersCount: T.number,
    hasOffers: T.bool,
    unreadResourcesCount: T.number,
    hasResources: T.bool,
    debug: T.object
};

internals.Wrapper = Styled.div`
    flex: 0 0 ${MAIN_FOOTER_HEIGHT}px;
    width: 100%;
    height: ${MAIN_FOOTER_HEIGHT}px;
    min-height: ${MAIN_FOOTER_HEIGHT}px;
    position: relative;
    z-index: 4;
    overflow: hidden;
    background-color: ${({ theme }) => theme.palette.primary.main};

    > div {
        height: ${MAIN_FOOTER_HEIGHT}px;
        width: 100%;
    }

    .MuiTabs-root {
        height: ${FOOTER_TAB_HEIGHT}px;
        min-height: ${FOOTER_TAB_HEIGHT}px;
    }

    .MuiTabs-flexContainer {
        height: 100%;
    }

    .MuiTab-root {
        flex: 1 1 0;
        max-width: none;
        min-width: 0;
        padding-left: 0;
        padding-right: 0;
    }

    .MuiTabs-indicator {
        display: block;
        position: absolute;
        bottom: 0;
        height: 3px;
        border-radius: 3px 3px 0 0;
        background-color: ${({ theme }) => theme.palette.secondary.main};
        transition: left 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, width 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    }
`;

internals.AppChromeBorderStyles = createGlobalStyle`
    .MuiAppBar-root.MuiAppBar-positionFixed {
        box-shadow: inset 0 -1px 0 ${({ theme }) => darken(theme.palette.primary.main, 0.34)} !important;
    }
`;

internals.IconWrapper = Styled.div`
    width: 35px;
    height: 35px;
    display: flex;
    align-items: center;
    justify-content: center;

    @supports (width: 1cqw) {
        width: clamp(30px, 10cqw, 35px);
        height: clamp(30px, 10cqw, 35px);
    }

    .MuiSvgIcon-root {
        max-width: 100%;
        max-height: 100%;
    }
`;

internals.StyledPaper = Styled(Paper)`
    display: flex;
    flex: 0 0 ${MAIN_FOOTER_HEIGHT}px;
    height: ${MAIN_FOOTER_HEIGHT}px;
    min-height: ${MAIN_FOOTER_HEIGHT}px;
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: $lightText !important;
    border-radius: 0 !important;
    transition: none !important;
    z-index: 4; // Above Snackbar and BottomButton
    box-shadow: inset 0 1px 0 ${({ theme }) => darken(theme.palette.primary.main, 0.34)};
    overflow: hidden;

    > div {
        flex: 1;
        align-items: center;
    }
`;

internals.TabsInnerWrapper = Styled.div`
    display: block;
    z-index: 3;
    container: app-footer-tabs / inline-size;
`;

internals.TabLabel = Styled.span`
    text-transform: none;
`;

internals.ProfileLinks = Styled.span`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    a { color: inherit; text-decoration: none; }
    > a:last-child { font-size: 11px; line-height: 13px; text-transform: none; white-space: nowrap; }
    ${({ $compact }) => $compact && `> a:first-child > div { height: 29px; }`}
`;

internals.StyledUnreadDMsBadge = Styled(UnreadDMsBadge)`
    .MuiBadge-badge {
        top: 4px;
        right: 2px;
    }
`;

internals.StyledUnreadClassMessagesBadge = Styled(UnreadClassMessagesBadge)`
    .MuiBadge-badge {
        top: 10px;
        right: 2px;
    }
`;

internals.StyledUnreadOffersBadge = Styled(UnreadMessagesBadge)`
    .MuiBadge-badge {
        top: 3px;
        right: -8px;
        min-width: 28px;
        height: 16px;
        padding: 0 4px;
        border-radius: 8px;
        font-size: 9px;
        line-height: 16px;
        font-weight: 700;
    }
`;

internals.StyledUnreadResourcesBadge = Styled(UnreadMessagesBadge)`
    .MuiBadge-badge {
        top: 3px;
        right: -8px;
        min-width: 28px;
        height: 16px;
        padding: 0 4px;
        border-radius: 8px;
        font-size: 9px;
        line-height: 16px;
        font-weight: 700;
    }
`;
